import React, { useState, useMemo } from 'react';
import { PricingPlan, CustomizationState } from '../types/pricing';

interface ServiceMatrixProps {
  plan: PricingPlan;
  isSelected: boolean;
  onSelect: (planSlug: string) => void;
  onCustomize: (customization: CustomizationState) => void;
  billingPeriod: 'monthly' | 'yearly';
}

const ServiceMatrix: React.FC<ServiceMatrixProps> = ({
  plan,
  isSelected,
  onSelect,
  onCustomize,
  billingPeriod,
}) => {
  const [disabledServices, setDisabledServices] = useState<string[]>([]);

  const currentPrice = useMemo(() => {
    const basePrice = billingPeriod === 'yearly' ? plan.priceYearly : plan.priceMonthly;
    const reduction = plan.services
      .filter(s => disabledServices.includes(s.id))
      .reduce((sum, s) => sum + s.priceReduction, 0);
    return Math.max(basePrice - reduction, 0);
  }, [plan, disabledServices, billingPeriod]);

  const handleToggleService = (serviceId: string) => {
    const service = plan.services.find(s => s.id === serviceId);
    if (!service || !service.canDisable) return;

    const newDisabled = disabledServices.includes(serviceId)
      ? disabledServices.filter(id => id !== serviceId)
      : [...disabledServices, serviceId];

    setDisabledServices(newDisabled);

    const basePrice = billingPeriod === 'yearly' ? plan.priceYearly : plan.priceMonthly;
    const reduction = plan.services
      .filter(s => newDisabled.includes(s.id))
      .reduce((sum, s) => sum + s.priceReduction, 0);

    onCustomize({
      planSlug: plan.slug,
      disabledServices: newDisabled,
      customPrice: Math.max(basePrice - reduction, 0),
      billingPeriod,
    });
  };

  const cardStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, rgba(13, 25, 48, 0.8) 0%, rgba(30, 58, 95, 0.6) 100%)',
    backdropFilter: 'blur(20px)',
    border: isSelected
      ? `2px solid ${plan.accentColor}`
      : `1px solid rgba(212, 175, 55, 0.3)`,
    borderRadius: '20px',
    padding: '32px 28px',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: isSelected
      ? `0 8px 32px ${plan.accentColor}44, 0 0 0 1px ${plan.accentColor}33`
      : '0 8px 32px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease',
  };

  const buttonBase: React.CSSProperties = {
    width: '100%',
    padding: '14px 24px',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s',
    border: 'none',
    fontFamily: 'var(--font-sans)',
  };

  const toggleStyle = (enabled: boolean): React.CSSProperties => ({
    width: '44px',
    height: '24px',
    borderRadius: '12px',
    background: enabled ? '#4fd1c5' : '#555',
    border: 'none',
    cursor: enabled ? 'pointer' : 'not-allowed',
    position: 'relative',
    transition: 'all 0.2s',
    opacity: enabled ? 1 : 0.5,
  });

  const toggleDot = (enabled: boolean): React.CSSProperties => ({
    position: 'absolute',
    top: '2px',
    left: enabled ? '22px' : '2px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: '#fff',
    transition: 'all 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  });

  const savings = billingPeriod === 'yearly' && plan.priceYearly > 0
    ? Math.round((plan.priceMonthly * 12 - plan.priceYearly) / (plan.priceMonthly * 12) * 100)
    : 0;

  return (
    <div style={cardStyle}>
      {plan.isPopular && (
        <div style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          background: 'rgba(212, 175, 55, 0.2)',
          border: '1px solid rgba(212, 175, 55, 0.6)',
          borderRadius: '8px',
          padding: '4px 10px',
          color: '#d4af37',
          fontSize: '12px',
          fontWeight: 600,
        }}>
          ⭐ Популярный
        </div>
      )}

      {savings > 0 && (
        <div style={{
          position: 'absolute',
          top: '12px',
          left: '12px',
          background: 'rgba(79, 209, 197, 0.2)',
          border: '1px solid rgba(79, 209, 197, 0.4)',
          borderRadius: '8px',
          padding: '4px 10px',
          color: '#4fd1c5',
          fontSize: '12px',
          fontWeight: 600,
        }}>
          -{savings}% за год
        </div>
      )}

      <h3 style={{ color: plan.accentColor, fontSize: '24px', marginBottom: '4px', fontFamily: 'var(--font-display)' }}>
        {plan.name}
      </h3>

      <p style={{ color: '#aaa', fontSize: '14px', marginBottom: '16px', fontStyle: 'italic' }}>
        {plan.tagline}
      </p>

      <div style={{ marginBottom: '20px' }}>
        <span style={{ color: '#fff', fontSize: '42px', fontWeight: 'bold' }}>
          {currentPrice}
        </span>
        <span style={{ color: '#aaa', fontSize: '16px' }}>
          {' '}₽{billingPeriod === 'yearly' ? '/год' : '/мес'}
        </span>
        {billingPeriod === 'yearly' && plan.priceYearly > 0 && (
          <div style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>
            {plan.priceYearly} ₽/год (≈{Math.round(plan.priceYearly / 12)} ₽/мес)
          </div>
        )}
      </div>

      {/* Services list with toggles */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ color: '#d4af37', fontSize: '13px', fontWeight: 600, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Что включено:
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {plan.services.map(service => {
            const isDisabled = disabledServices.includes(service.id);
            const canToggle = service.canDisable && plan.slug !== 'free';

            return (
              <div
                key={service.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  opacity: isDisabled ? 0.5 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                <span style={{ fontSize: '18px' }}>{service.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fff', fontSize: '14px', fontWeight: 500 }}>
                    {service.name}
                  </div>
                  <div style={{ color: '#888', fontSize: '12px' }}>
                    {service.description}
                  </div>
                </div>
                {service.value >= 4 && (
                  <span style={{ color: '#d4af37', fontSize: '12px' }}>
                    {'⭐'.repeat(Math.min(service.value, 5))}
                  </span>
                )}
                {canToggle && (
                  <button
                    onClick={() => handleToggleService(service.id)}
                    style={toggleStyle(!isDisabled)}
                    aria-label={`${isDisabled ? 'Включить' : 'Отключить'} ${service.name}`}
                    title={isDisabled ? `Включить (-${service.priceReduction} ₽)` : `Отключить (-${service.priceReduction} ₽)`}
                  >
                    <span style={toggleDot(!isDisabled)} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {disabledServices.length > 0 && (
          <div style={{
            marginTop: '12px',
            padding: '8px 12px',
            background: 'rgba(79, 209, 197, 0.1)',
            border: '1px solid rgba(79, 209, 197, 0.3)',
            borderRadius: '8px',
            color: '#4fd1c5',
            fontSize: '13px',
          }}>
            Отключено услуг: {disabledServices.length} · Экономия: {disabledServices.reduce((sum, id) => {
              const s = plan.services.find(s => s.id === id);
              return sum + (s?.priceReduction || 0);
            }, 0)} ₽
          </div>
        )}
      </div>

      <button
        onClick={() => onSelect(plan.slug)}
        style={{
          ...buttonBase,
          background: isSelected
            ? `linear-gradient(135deg, ${plan.accentColor} 0%, ${plan.accentColor}cc 100%)`
            : `linear-gradient(135deg, rgba(${plan.accentColor}22) 0%, rgba(${plan.accentColor}11) 100%)`,
          color: isSelected ? '#0a0a1a' : plan.accentColor,
          border: isSelected ? 'none' : `1px solid ${plan.accentColor}44`,
        }}
      >
        {plan.ctaText}
      </button>
    </div>
  );
};

export default ServiceMatrix;
